/**
 * useAddonCalculations - Calculates prices for all additional services (addons)
 * based on settings and selected addon state.
 */
import { useMemo } from 'react';
import {
  DISTRIBUTION_PACKAGES,
  BOOKTRAILER_PACKAGES,
  LEGAL_KIT_OPTIONS,
  COVER_DESIGN_OPTIONS,
  BRANDING_PACK_OPTIONS
} from './addonConstants';

export function useAddonCalculations(settings, addonState) {
  const {
    selectedDistribution, selectedBooktrailer, selectedLegalKit, selectedCoverDesign,
    selectedBrandingPack, selectedMusicStock, musicStockQuantity,
    selectedConsultoria, consultoriaQuantity,
    selectedQrDinamico, selectedEnlaceGo, selectedNfcVcard, selectedSeparadores,
    selectedPreventa, selectedBannerPromotion, bannerPromotionMonths,
    selectedDisenoWebBanner, selectedPhotoshoot, selectedPaginaAutor,
    selectedPautajeDigital,
    selectedCommissionFree,
    selectedTarjetas, selectedSeparadoresImpresos, selectedEnvio
  } = addonState;

  return useMemo(() => {
    const getPrice = (id, map) => map[id] || 0;

    const distPrices = {
      'dist_essential': settings.price_dist_essential || 697,
      'dist_platino': settings.price_dist_platino || 1697,
      'dist_premium': settings.price_dist_premium || 2997
    };
    const btPrices = {
      'bt_essential': settings.price_bt_essential || 697,
      'bt_platino': settings.price_bt_platino || 4997,
      'bt_premium': settings.price_bt_premium || 8997
    };
    const legalPrices = {
      'legal_registro': settings.price_legal_registro || 2500,
      'legal_isbn': settings.price_legal_isbn || 2000,
      'legal_combo': settings.price_legal_combo || 4000
    };
    const brandingPrices = {
      'branding_essential': settings.price_branding_essential || 2500,
      'branding_pro': settings.price_branding_pro || 4500,
      'branding_premium': settings.price_branding_premium || 7500
    };

    const musicUnitPrice = settings.price_music_stock || 797;
    const consultoriaUnitPrice = settings.price_consultoria || 850;
    const qrDinamicoPrice = settings.price_qr_dinamico || 397;
    const enlaceGoPrice = settings.price_enlace_go || 197;
    const nfcVcardPrice = settings.price_nfc_vcard || 1997;
    const separadoresPrice = settings.price_separadores || 397;
    const preventaPrice = settings.price_preventa || 897;
    const bannerPromotionMonthPrice = settings.price_banner_promotion || 197;
    const disenoWebBannerPrice = settings.price_diseno_web_banner || 997;
    const photoshootPrice = settings.price_photoshoot || 2500;
    const paginaAutorPrice = settings.price_pagina_autor || 3500;
    const pautajeDigitalPrice = settings.price_pautaje_digital || 2000;
    const tarjetasPrice = settings.price_tarjetas || 697;
    const separadoresImpresosPrice = settings.price_separadores_impresos || 797;
    const envioPrice = settings.price_envio || 150;

    const makeAddon = (id, noneId, list, priceGetter) => {
      if (id !== noneId) {
        const found = list?.find(p => p.id === id);
        return { id, name: found?.name || id, price: priceGetter(id) };
      }
      return { id: noneId, name: '', price: 0 };
    };

    const distributionAddon = makeAddon(selectedDistribution, 'dist_none', DISTRIBUTION_PACKAGES, id => getPrice(id, distPrices));
    const booktrailerAddon = makeAddon(selectedBooktrailer, 'bt_none', BOOKTRAILER_PACKAGES, id => getPrice(id, btPrices));
    const legalKitAddon = makeAddon(selectedLegalKit, 'legal_none', LEGAL_KIT_OPTIONS, id => getPrice(id, legalPrices));
    const coverDesignAddon = selectedCoverDesign === 'cover_design'
      ? { id: 'cover_design', name: 'Diseño de Portada', price: settings.price_cover_design || 1250 }
      : { id: 'cover_none', name: '', price: 0 };
    const brandingPackAddon = makeAddon(selectedBrandingPack, 'branding_none', BRANDING_PACK_OPTIONS, id => getPrice(id, brandingPrices));
    const musicStockAddon = selectedMusicStock === 'music_stock'
      ? { id: 'music_stock', name: `Música de Stock (${musicStockQuantity} pieza${musicStockQuantity > 1 ? 's' : ''})`, price: musicUnitPrice * musicStockQuantity }
      : { id: 'music_none', name: '', price: 0 };
    const consultoriaAddon = selectedConsultoria === 'consultoria'
      ? { id: 'consultoria', name: `Consultoría Personalizada (${consultoriaQuantity} sesión${consultoriaQuantity > 1 ? 'es' : ''})`, price: consultoriaUnitPrice * consultoriaQuantity }
      : { id: 'consultoria_none', name: '', price: 0 };

    const qrDinamicoAddon = selectedQrDinamico === 'qr_dinamico' ? { id: 'qr_dinamico', name: 'QR Dinámico', price: qrDinamicoPrice } : { id: 'qr_none', name: '', price: 0 };
    const enlaceGoAddon = selectedEnlaceGo === 'enlace_go' ? { id: 'enlace_go', name: 'Enlace Único Go.Viubux.com', price: enlaceGoPrice } : { id: 'enlace_none', name: '', price: 0 };
    const nfcVcardAddon = selectedNfcVcard === 'nfc_vcard' ? { id: 'nfc_vcard', name: 'Digital NFC VCard / LinkTree', price: nfcVcardPrice } : { id: 'nfc_none', name: '', price: 0 };
    const separadoresAddon = selectedSeparadores === 'separadores' ? { id: 'separadores', name: 'Separadores de Libro para Imprimir', price: separadoresPrice } : { id: 'sep_none', name: '', price: 0 };
    const preventaAddon = selectedPreventa === 'preventa' ? { id: 'preventa', name: 'Activar Preventa', price: preventaPrice } : { id: 'preventa_none', name: '', price: 0 };
    const bannerPromotionAddon = selectedBannerPromotion === 'banner_promotion'
      ? { id: 'banner_promotion', name: `Banner Promotion (${bannerPromotionMonths} mes${bannerPromotionMonths > 1 ? 'es' : ''})`, price: bannerPromotionMonthPrice * bannerPromotionMonths }
      : { id: 'banner_none', name: '', price: 0 };
    const disenoWebBannerAddon = selectedDisenoWebBanner === 'diseno_web_banner' ? { id: 'diseno_web_banner', name: 'Diseño de WEB Banner', price: disenoWebBannerPrice } : { id: 'web_banner_none', name: '', price: 0 };
    const photoshootAddon = selectedPhotoshoot === 'photoshoot' ? { id: 'photoshoot', name: 'Photoshoot 1hr', price: photoshootPrice } : { id: 'photoshoot_none', name: '', price: 0 };
    const paginaAutorAddon = selectedPaginaAutor === 'pagina_autor' ? { id: 'pagina_autor', name: 'Página de Autor', price: paginaAutorPrice } : { id: 'pagina_autor_none', name: '', price: 0 };
    const pautajeDigitalAddon = selectedPautajeDigital === 'pautaje_digital' ? { id: 'pautaje_digital', name: 'Pautaje Digital en Redes Sociales y Google', price: pautajeDigitalPrice } : { id: 'pautaje_none', name: '', price: 0 };
    const commissionFreePrice = settings.price_commission_free || 1500;
    const commissionFreeAddon = selectedCommissionFree === 'commission_free' ? { id: 'commission_free', name: 'Commission-Free Monthly Pass', price: commissionFreePrice } : { id: 'commission_free_none', name: '', price: 0 };
    const tarjetasAddon = selectedTarjetas === 'tarjetas' ? { id: 'tarjetas', name: '1000 Tarjetas de Presentación 4x4', price: tarjetasPrice } : { id: 'tarjetas_none', name: '', price: 0 };
    const separadoresImpresosAddon = selectedSeparadoresImpresos === 'sep_impresos' ? { id: 'sep_impresos', name: 'Separadores de Libro Impresos (500 pzas)', price: separadoresImpresosPrice } : { id: 'sep_impresos_none', name: '', price: 0 };
    const envioAddon = selectedEnvio === 'envio' ? { id: 'envio', name: 'Envío a Domicilio', price: envioPrice } : { id: 'envio_none', name: '', price: 0 };

    const allAddons = {
      distributionAddon, booktrailerAddon, legalKitAddon, coverDesignAddon,
      brandingPackAddon, musicStockAddon, consultoriaAddon,
      qrDinamicoAddon, enlaceGoAddon, nfcVcardAddon, separadoresAddon,
      preventaAddon, bannerPromotionAddon, disenoWebBannerAddon,
      photoshootAddon, paginaAutorAddon, pautajeDigitalAddon, commissionFreeAddon,
      tarjetasAddon, separadoresImpresosAddon, envioAddon
    };

    const total = Object.values(allAddons).reduce((sum, a) => sum + a.price, 0);

    return { ...allAddons, audiobookAddonsTotal: total };
  }, [settings, selectedDistribution, selectedBooktrailer, selectedLegalKit, selectedCoverDesign,
    selectedBrandingPack, selectedMusicStock, musicStockQuantity,
    selectedConsultoria, consultoriaQuantity,
    selectedQrDinamico, selectedEnlaceGo, selectedNfcVcard, selectedSeparadores,
    selectedPreventa, selectedBannerPromotion, bannerPromotionMonths,
    selectedDisenoWebBanner, selectedPhotoshoot, selectedPaginaAutor, selectedPautajeDigital,
    selectedCommissionFree,
    selectedTarjetas, selectedSeparadoresImpresos, selectedEnvio]);
}
